import React, { useEffect } from "react";
import { Form } from "react-bootstrap";
import { GuildJoinValues, GuildLanguages } from "../data";
import Button from "../../../components/Button";
import { Formik } from "formik";
import { GuildJoinTypeEnum } from "../types";
import Network from "../../../utils/Network";
import useToast from "../../../hooks/useToast";
import GuildCrest from "../components/GuildCrest";
import { DEFAULT_GUILD_CREST, DEFAULT_GUILD_HALL, guildAsset } from "../utils";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import { formatNumber } from "../../../i18n/formatters";

type GuildCreateType = {
  id?: string;
  name: string;
  description: string;
  language: string;
  joinType: GuildJoinTypeEnum;
  image: string;
  banner: string;
  minRankScore: number;
};
type Props = {
  initialValue?: GuildCreateType;
  onSuccess: () => void;
  /** The hall picked so far, shown behind the page while the form is open. */
  onTryHall?: (banner: string | null) => void;
  images?: {
    images: string[];
    banners: string[];
  };
};

type PickerProps = {
  label: string;
  options?: string[];
  value: string;
  halls?: boolean;
  onPick: (src: string) => void;
};

function Picker({ label, options, value, halls, onPick }: PickerProps) {
  const { t } = useAppTranslation("guild");
  const list = (options || []).filter(Boolean);
  return (
    <div>
      <div className="gh-field-label">{label}</div>
      <div className={halls ? "gh-picks is-halls" : "gh-picks"}>
        {list.map((src, index) => (
          <button
            key={src}
            type="button"
            className="gh-pick"
            aria-pressed={src === value}
            aria-label={t("guild:build.pickers.option", { label, number: formatNumber(index + 1) })}
            onClick={() => onPick(src)}
          >
            <img src={guildAsset(src, halls ? DEFAULT_GUILD_HALL : DEFAULT_GUILD_CREST)} alt="" loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  );
}

// Tells the page which hall is picked, so the room changes as soon as one is chosen.
function HallPreview({ banner, onTryHall }: { banner: string; onTryHall?: Props["onTryHall"] }) {
  useEffect(() => {
    onTryHall?.(banner || null);
  }, [banner]);
  return null;
}

const Build = ({ onSuccess, initialValue, images, onTryHall }: Props) => {
  const axios = new Network();
  const openToast = useToast();
  const { t } = useAppTranslation(["guild", "common"]);
  const isUpdateMode = Boolean(initialValue);

  const initialValues: GuildCreateType = {
    name: initialValue?.name ?? "",
    description: initialValue?.description ?? "",
    language: initialValue?.language ?? GuildLanguages[0],
    joinType: initialValue?.joinType ?? GuildJoinTypeEnum.Public,
    image: initialValue?.image ?? "",
    banner: initialValue?.banner ?? "",
    minRankScore: initialValue?.minRankScore ?? 0,
  };

  const handleCreateGuild = async (body: GuildCreateType) => {
    const response = await axios.CallApi({ url: "/guild/create", body });
    openToast(response);
    if (response.ok) {
      onSuccess();
    }
  };

  const handleUpdateGuild = async (body: GuildCreateType) => {
    const response = await axios.CallApi({
      url: `/guild/${initialValue?.id}/update`,
      body,
    });
    openToast(response);
    if (response.ok) {
      onSuccess();
    }
  };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(v: GuildCreateType, { setSubmitting }) =>
        !isUpdateMode
          ? handleCreateGuild(v).then(() => setSubmitting(false))
          : handleUpdateGuild(v).then(() => setSubmitting(false))
      }
    >
      {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
        <form onSubmit={handleSubmit} className="gh-build">
          <HallPreview banner={values.banner} onTryHall={onTryHall} />
          <div className="gh-build-side gh-panel">
            <div
              className="gh-preview"
              style={{ backgroundImage: `url("${guildAsset(values.banner, DEFAULT_GUILD_HALL)}")` }}
            >
              <GuildCrest image={values.image} />
              <div style={{ minWidth: 0 }}>
                <div className="gh-preview-name">{values.name || t("guild:build.preview.defaultName")}</div>
                {values.description && <div className="gh-preview-about">{values.description}</div>}
              </div>
            </div>
            <Picker
              label={t("guild:build.pickers.crest")}
              options={images?.images}
              value={values.image}
              onPick={(src) => setFieldValue("image", src)}
            />
            <Picker
              label={t("guild:build.pickers.hall")}
              halls
              options={images?.banners}
              value={values.banner}
              onPick={(src) => setFieldValue("banner", src)}
            />
          </div>

          <div className="gh-build-form gh-panel">
            <div>
              <label className="gh-field-label" htmlFor="gh-name">
                {t("guild:build.form.name")}
              </label>
              <Form.Control id="gh-name" name={"name"} value={values.name} onChange={handleChange} />
            </div>
            <div className="gh-field-row">
              <div>
                <label className="gh-field-label" htmlFor="gh-type">
                  <img src={"/assets/images/icons/letter.png"} alt="" />
                  {t("guild:build.form.type")}
                </label>
                <Form.Select
                  id="gh-type"
                  name={"joinType"}
                  value={values.joinType}
                  onChange={(e) => setFieldValue("joinType", +e.target.value)}
                >
                  <option disabled value="">
                    {t("guild:build.form.choose")}
                  </option>
                  {Object.entries(GuildJoinValues).map(([key, value]) => (
                    <option key={key} value={+key}>
                      {t(`guild:explore.joinTypes.${key}`, {
                        defaultValue: value,
                      })}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div>
                <label className="gh-field-label" htmlFor="gh-language">
                  <img src={"/assets/images/icons/earth.png"} alt="" />
                  {t("guild:build.form.language")}
                </label>
                <Form.Select id="gh-language" name={"language"} value={values.language} onChange={handleChange}>
                  <option disabled value="">
                    {t("guild:build.form.choose")}
                  </option>
                  {GuildLanguages.map((lang) => (
                    <option key={lang} value={lang}>
                      {t(`guild:explore.languages.${lang}`, {
                        defaultValue: lang,
                      })}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div>
                <label className="gh-field-label" htmlFor="gh-score">
                  <img src={"/assets/images/icons/glory.png"} alt="" />
                  {t("guild:build.form.minScore")}
                </label>
                <Form.Control
                  id="gh-score"
                  name={"minRankScore"}
                  inputMode="numeric"
                  value={values.minRankScore}
                  onChange={handleChange}
                  placeholder={t("guild:build.form.minScorePlaceholder")}
                />
              </div>
            </div>
            <div>
              <label className="gh-field-label" htmlFor="gh-about">
                {t("guild:build.form.description")}
              </label>
              <Form.Control
                id="gh-about"
                as="textarea"
                rows={5}
                name={"description"}
                value={values.description}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="gh-build-submit">
            <Button loading={isSubmitting} type="submit" variant="wide-success">
              {isUpdateMode ? t("common:actions.saveChanges") : t("guild:build.actions.createGuild")}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default Build;
